import { ComponentRef, ReactNode, useRef, useState } from 'react';

import { Input } from '~/modules/elements/input';
import { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import { getRemoteMicHostname, isValidOrigin, normaliseOrigin } from '~/modules/utils/remote-mic-origin';
import { ControlGroup } from '~/routes/keyboard-help/controls';
import { RemoteMicOriginSetting, useSettingValue } from '~/routes/settings/settings-state';

type RegisterProps = ReturnType<RegisterFunc>;

// `Input` forwards unknown props to the DOM, and the nav handler is not a valid attribute.
const withoutNavHandler = (props: RegisterProps) => {
  const { $keyboardNavigationChangeFocus: _navHandler, ...rest } = props as RegisterProps &
    Record<'$keyboardNavigationChangeFocus', unknown>;

  return rest;
};

interface Props {
  register: RegisterFunc;
  name: string;
  /** Rendered as-is, so the caller can hang the explanation off it as an `InfoGlyph`. */
  label: ReactNode;
  /** The plain-text name used for the keyboard help and the phone's own copy of this control. */
  remoteLabel: string;
  /** Printed underneath the field. Settings carries its explanation in `label` as a glyph instead. */
  info?: ReactNode;
  isDefault?: boolean;
  /** Cards this row with the rest of its group on the remote - see `ControlGroup`. */
  group?: ControlGroup;
}

function PhoneLinkAddressField({ register, name, label, remoteLabel, info, isDefault = false, group }: Props) {
  const [remoteMicOrigin, setRemoteMicOrigin] = useSettingValue(RemoteMicOriginSetting);
  // A field being typed into passes through text that is not yet an address, and the setting only
  // ever holds one that is - so the draft cannot be the setting itself. This was a `prompt()` until
  // it turned out Electron does not implement one, leaving the desktop app unable to set the single
  // thing a fresh install cannot work out for itself.
  const [draft, setDraft] = useState(remoteMicOrigin);
  const inputRef = useRef<ComponentRef<typeof Input>>(null);

  const change = (value: string) => {
    setDraft(value);

    const normalised = normaliseOrigin(value);
    // Empty clears the override and goes back to AUTO. Half-typed text is left uncommitted rather
    // than rejected, so the field does not fight the person filling it in.
    if (normalised === '' || isValidOrigin(normalised)) {
      setRemoteMicOrigin(normalised);
    }
  };

  // Said on the way out rather than per keystroke: `https://` is only wrong once someone has
  // stopped typing it. Off loopback a phone needs a secure context to open its microphone, so an
  // http address loads the page and then fails at the one thing the page is for.
  const reportUncommitted = () => {
    const normalised = normaliseOrigin(draft);

    if (normalised !== '' && !isValidOrigin(normalised)) {
      inputRef.current?.triggerValidationError('Needs to start with https://');
    }
  };

  return (
    <Input
      label={label}
      info={info}
      // `ButtonBase` is bold uppercase, which is right for a button and wrong for a settings row -
      // this one has to read like the switchers above and below it, with the address on the right.
      className="h-[3.375rem] text-[1.25rem] font-normal normal-case [&_input]:text-right [&_input::placeholder]:text-white/40"
      value={draft}
      onChange={change}
      onBlur={reportUncommitted}
      // An empty field still has to say what is in effect, which is what the switcher this replaced
      // showed in place of a value.
      placeholder={`AUTO (${getRemoteMicHostname()})`}
      ref={inputRef}
      {...withoutNavHandler(
        register(name, () => inputRef.current?.element?.focus(), remoteLabel, isDefault, {
          control: {
            type: 'text',
            label: remoteLabel,
            value: draft,
            placeholder: `AUTO (${getRemoteMicHostname()})`,
            group,
          },
          onValueChange: change,
        }),
      )}
    />
  );
}

export default PhoneLinkAddressField;
